import React, { useState, useEffect, useCallback } from 'react'
import { Tooltip } from '@mantine/core'
import { AppIconButton, AppText } from '../../app/common/ui'
import {
  IoHeart,
  IoHeartOutline,
  IoShareSocialOutline,
  IoChatbubbleOutline
} from 'react-icons/io5'
import { useHistory } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import { BlogDiscoverCard } from '@gunslop/ui'

export interface BlogDto {
  id: string;
  userId: string;
  title: string;
  excerpt: string;
  cleverTags: string[];
  createdAt: string;
  userAvatar: string;
  userName: string;
  likeCount: number;
  thumbnail?: string;
}

interface BlogCardProps {
  blog: BlogDto;
  onLike?: (blogId: string, liked: boolean) => Promise<void>;
  onShare?: (blogId: string) => void;
}

function getRelativeTime (dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime()
  const minutes = Math.floor(diffMs / (1000 * 60))
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

export const BlogCard: React.FC<BlogCardProps> = ({ blog, onLike, onShare }) => {
  const history = useHistory()
  const { voteStore } = useStore()
  const [isLiked, setIsLiked] = useState(false)
  const [isLoadingLike, setIsLoadingLike] = useState(false)
  const [likeCount, setLikeCount] = useState(blog.likeCount)

  useEffect(() => {
    setLikeCount(blog.likeCount)
  }, [blog.id, blog.likeCount])

  const handleNavigate = () => {
    history.push(`/blog/${blog.id}`)
  }

  const handleLike = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      if (isLoadingLike) return
      setIsLoadingLike(true)
      const prevLiked = isLiked
      const prevCount = likeCount
      try {
        if (onLike) {
          const next = !isLiked
          setIsLiked(next)
          await onLike(blog.id, next)
        } else {
          if (isLiked) {
            await voteStore.removeVote(agent.VoteTargetType.Post, blog.id)
            setIsLiked(false)
            setLikeCount((c) => Math.max(0, c - 1))
          } else {
            await voteStore.castVote(agent.VoteTargetType.Post, blog.id, 1)
            setIsLiked(true)
            setLikeCount((c) => c + 1)
          }
        }
      } catch (error) {
        setIsLiked(prevLiked)
        setLikeCount(prevCount)
        console.error('Failed to like blog:', error)
      } finally {
        setIsLoadingLike(false)
      }
    },
    [blog.id, isLiked, isLoadingLike, likeCount, onLike, voteStore]
  )

  const actionBar = (
    <>
      <AppIconButton
        variant="subtle"
        size="lg"
        color={isLiked ? 'red' : 'gray'}
        onClick={(e) => void handleLike(e)}
        disabled={isLoadingLike}
        aria-label={isLiked ? 'Remove upvote' : 'Upvote'}
      >
        {isLiked ? <IoHeart size={22} /> : <IoHeartOutline size={22} />}
      </AppIconButton>

      <Tooltip label="Comments open on the blog post" withArrow position="top">
        <AppIconButton variant="subtle" size="lg" color="gray" disabled aria-label="Comments (disabled)">
          <IoChatbubbleOutline size={22} style={{ opacity: 0.45 }} />
        </AppIconButton>
      </Tooltip>

      <AppIconButton
        variant="subtle"
        size="lg"
        color="gray"
        onClick={() => onShare?.(blog.id)}
        aria-label="Share"
        sx={{ marginLeft: 'auto' }}
      >
        <IoShareSocialOutline size={22} />
      </AppIconButton>

      <AppText size="xs" c="dimmed" style={{ flexShrink: 0 }}>
        {likeCount} {likeCount === 1 ? 'like' : 'likes'}
      </AppText>
    </>
  )

  return (
    <BlogDiscoverCard
      title={blog.title}
      excerpt={blog.excerpt}
      tags={blog.cleverTags}
      userName={blog.userName}
      userAvatar={blog.userAvatar}
      timeLabel={getRelativeTime(blog.createdAt)}
      thumbnailUrl={blog.thumbnail}
      onOpen={handleNavigate}
      actionBar={actionBar}
    />
  )
}

export default BlogCard
